import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Share2, Download } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { JAR_TYPES, JAR_TYPE_ORDER } from './three/jarMeta';
import { renderKitShareImage, canvasToBlob } from '../lib/kitShareImage';
import { splitEffects } from '../lib/kitLogic';
import { API } from '../constants';
import { logError, isUserCancel } from '../lib/log';

const IgIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="2.5" y="2.5" width="19" height="19" rx="5.5" />
    <circle cx="12" cy="12" r="4.2" />
    <circle cx="17.4" cy="6.6" r="1.1" fill="currentColor" stroke="none" />
  </svg>
);

const TtIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden>
    <path d="M16.6 3c.32 2.06 1.5 3.63 3.5 4.06v2.62c-1.32 0-2.55-.42-3.55-1.05v6.6c0 3.44-2.72 5.77-5.7 5.77A5.65 5.65 0 0 1 5 15.35a5.6 5.6 0 0 1 5.6-5.4c.32 0 .63.03.93.09v2.77a2.83 2.83 0 0 0-.93-.16 2.84 2.84 0 1 0 2.84 2.84V3h3.16z" />
  </svg>
);

const ShareKitModal = ({ kit, onClose }) => {
  const { t } = useLanguage();
  const [url, setUrl] = useState(null);
  const [blob, setBlob] = useState(null);
  const [name, setName] = useState('');
  const [creator, setCreator] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);
  const [wasPublic, setWasPublic] = useState(true);
  const [publishedId, setPublishedId] = useState(null);

  const displayName = name.trim() || t('share.default_name');
  const shareLink = publishedId ? `${window.location.origin}/?kit=${publishedId}` : '';

  useEffect(() => {
    if (!kit) return undefined;
    let alive = true;
    let objectUrl = null;
    const timer = setTimeout(async () => {
      const canvas = await renderKitShareImage({
        name: displayName,
        fn: kit.fn,
        items: kit.items,
        labels: {
          typeNames: Object.fromEntries(JAR_TYPE_ORDER.map((tp) => [tp, t(JAR_TYPES[tp].nameKey)])),
          tagline: t('share.tagline'),
          volume: t('home.mix.volume'),
          hashtags: '#BeautyKit  #JustMix',
          site: window.location.host,
        },
      });
      const b = await canvasToBlob(canvas);
      if (!alive || !b) return;
      objectUrl = URL.createObjectURL(b);
      setBlob(b);
      setUrl(objectUrl);
    }, url ? 450 : 0);
    return () => { alive = false; clearTimeout(timer); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [kit, t, name]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!kit) return null;

  const fileName = `beautykit-${(name.trim() || 'just-mix').toLowerCase().replace(/[^a-z0-9]+/g, '-')}.jpg`;
  const download = () => {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
  };

  const share = async () => {
    if (!blob) return;
    const file = new File([blob], fileName, { type: 'image/jpeg' });
    const text = t('share.text').replace('{fn}', kit.fn || 'BeautyKit');
    if (navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: 'BeautyKit', text });
      } catch (error) {
        if (!isUserCancel(error)) { logError('Share failed:', error); download(); toast.message(t('share.downloaded')); }
      }
    } else {
      download();
      toast.message(t('share.downloaded'));
    }
  };

  const toSocial = (platform) => {
    download();
    const links = {
      instagram: 'https://www.instagram.com/',
      tiktok: 'https://www.tiktok.com/upload',
    };
    toast.message(platform === 'instagram' ? t('share.ig_toast') : t('share.tt_toast'));
    setTimeout(() => window.open(links[platform], '_blank', 'noopener'), 350);
  };

  const copyLink = async () => {
    if (!shareLink) return;
    try { await navigator.clipboard.writeText(shareLink); toast.success(t('share.link_copied')); }
    catch (error) { logError('Clipboard unavailable:', error); toast.message(shareLink); }
  };

  const publish = async () => {
    const n = name.trim();
    if (n.length < 3) { toast.warning(t('share.name_required')); return; }
    setPublishing(true);
    try {
      const objectives = [...new Set(kit.items.flatMap((i) => splitEffects(i)))];
      const total = kit.total ?? kit.items.reduce((s, i) => s + (i.price || 0), 0);
      const res = await axios.post(`${API}/beautykit`, {
        name: n,
        creator_name: creator.trim() || 'Anonimo',
        skin_type: 'mix',
        objectives,
        products: kit.items.map((i) => i.id),
        duration_days: 30,
        quantities: Object.fromEntries(kit.items.map((i) => [i.id, 1])),
        total_price: total,
        items: kit.items.map((i) => ({ product_type: i.product_type, line_color: i.line_color, main_ingredient: i.main_ingredient, effect: i.effect })),
        is_public: isPublic,
      });
      setPublishedId(res.data.beautykit_id);
      setWasPublic(isPublic);
      setPublished(true);
      toast.success(isPublic ? t('share.published_public') : t('share.published_private'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('share.publish_error'));
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[60] flex items-center justify-center p-4" data-testid="share-kit-modal">
      <motion.div className="share-modal" initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}>
        <button onClick={onClose} className="share-close" aria-label={t('share.close')} data-testid="share-kit-close">✕</button>
        <div className="share-preview">
          {url ? (
            <img src={url} alt="BeautyKit" data-testid="share-kit-image" />
          ) : (
            <div className="share-loading" data-testid="share-kit-loading"><span /><p>{t('share.generating')}</p></div>
          )}
        </div>
        <div className="share-body">
          <span className="eyebrow">{t('share.title')}</span>
          <h3 className="share-title text-gradient-brand">{displayName}</h3>
          <p className="share-sub">{t('share.subtitle')}</p>

          {!published ? (
            <div className="share-form" data-testid="share-kit-form">
              <label className="share-field">
                <span>{t('share.name_label')}</span>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('share.name_placeholder')}
                  maxLength={40}
                  data-testid="share-kit-name-input"
                />
              </label>
              <label className="share-field">
                <span>{t('share.creator_label')}</span>
                <input
                  value={creator}
                  onChange={(e) => setCreator(e.target.value)}
                  placeholder={t('share.creator_placeholder')}
                  maxLength={30}
                  data-testid="share-kit-creator-input"
                />
              </label>
              <button
                type="button"
                className={`share-toggle ${isPublic ? 'on' : ''}`}
                onClick={() => setIsPublic((v) => !v)}
                role="switch"
                aria-checked={isPublic}
                data-testid="share-kit-public-toggle"
              >
                <span className="share-toggle-track"><span className="share-toggle-thumb" /></span>
                <span className="share-toggle-text">
                  <strong>{t('share.public_label')}</strong>
                  <small>{t('share.public_hint')}</small>
                </span>
              </button>
              <button onClick={publish} disabled={publishing} className="btn-pill btn-pill-primary share-publish" data-testid="share-kit-publish">
                {publishing ? t('share.publishing') : isPublic ? t('share.publish') : t('share.save_private')}
              </button>
            </div>
          ) : (
            <div className="share-published" data-testid="share-kit-published">
              <span className="share-published-icon">✓</span>
              <p>{wasPublic ? t('share.published_public') : t('share.published_private')}</p>
            </div>
          )}

          {publishedId && (
            <div className="share-link" data-testid="share-kit-link-wrap">
              <span>{t('share.link_label')}</span>
              <div className="share-link-row">
                <input readOnly value={shareLink} onFocus={(e) => e.target.select()} data-testid="share-kit-link" />
                <button onClick={copyLink} className="btn-pill btn-pill-ghost" data-testid="share-kit-copy-link">{t('share.copy_link')}</button>
              </div>
              <small className="share-link-hint">{t('share.link_hint')}</small>
            </div>
          )}

          <div className="share-social">
            <p className="share-social-hint">{t('share.social_hint')}</p>
            <div className="share-social-row">
              <button onClick={() => toSocial('instagram')} disabled={!url} className="share-social-btn ig" aria-label="Instagram" data-testid="share-kit-instagram"><IgIcon /></button>
              <button onClick={() => toSocial('tiktok')} disabled={!url} className="share-social-btn tt" aria-label="TikTok" data-testid="share-kit-tiktok"><TtIcon /></button>
              <button onClick={share} disabled={!blob} className="share-social-btn" aria-label={t('share.share')} data-testid="share-kit-share"><Share2 size={18} /></button>
              <button onClick={download} disabled={!url} className="share-social-btn" aria-label={t('share.download')} data-testid="share-kit-download"><Download size={18} /></button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ShareKitModal;
